import clsx from "clsx";
import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]:not([tabindex='-1'])",
  "button:not([disabled]):not([tabindex='-1'])",
  "input:not([disabled]):not([tabindex='-1'])",
  "select:not([disabled]):not([tabindex='-1'])",
  "textarea:not([disabled]):not([tabindex='-1'])",
  "[contenteditable='true']:not([tabindex='-1'])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

const dialogStack: symbol[] = [];

export interface DialogShellProps {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  closeLabel?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
}

export function DialogShell({
  ariaLabel,
  children,
  className,
  backdropClassName,
  closeLabel = "关闭弹窗",
  closeOnBackdrop = false,
  closeOnEscape = true,
  onClose,
  showCloseButton = false
}: DialogShellProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const dialogIdRef = useRef(Symbol("dialog-shell"));
  const closeOnEscapeRef = useRef(closeOnEscape);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    closeOnEscapeRef.current = closeOnEscape;
  }, [closeOnEscape]);

  useEffect(() => {
    const dialogId = dialogIdRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    dialogStack.push(dialogId);

    const focusFrame = window.requestAnimationFrame(() => {
      if (dialogStack.at(-1) !== dialogId) {
        return;
      }

      const dialog = dialogRef.current;
      const firstFocusable = dialog ? getFocusableElements(dialog)[0] : null;
      focusWithoutScrolling(firstFocusable ?? dialog);
    });

    function handleKeyDown(event: KeyboardEvent) {
      const dialog = dialogRef.current;

      if (!dialog) {
        return;
      }

      if (dialogStack.at(-1) !== dialogId) {
        return;
      }

      if (event.key === "Escape" && closeOnEscapeRef.current) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(dialog);

      if (focusable.length === 0) {
        event.preventDefault();
        focusWithoutScrolling(dialog);
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || !dialog.contains(activeElement))) {
        event.preventDefault();
        focusWithoutScrolling(last);
      } else if (!event.shiftKey && (activeElement === last || !dialog.contains(activeElement))) {
        event.preventDefault();
        focusWithoutScrolling(first);
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown, true);
      const wasTopDialog = dialogStack.at(-1) === dialogId;
      const stackIndex = dialogStack.lastIndexOf(dialogId);

      if (stackIndex >= 0) {
        dialogStack.splice(stackIndex, 1);
      }

      if (wasTopDialog) {
        focusWithoutScrolling(previouslyFocused);
      }
    };
  }, []);

  return (
    <div
      className={clsx("modal-backdrop", "dialog-shell__backdrop", backdropClassName)}
      onPointerDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        ref={dialogRef}
        className={clsx("dialog-shell", className)}
        role="dialog"
        aria-label={ariaLabel}
        aria-modal="true"
        tabIndex={-1}
      >
        {showCloseButton ? (
          <button
            type="button"
            className="dialog-shell__close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X size={20} />
          </button>
        ) : null}
        {children}
      </section>
    </div>
  );
}

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => {
      if (
        element.tabIndex < 0 ||
        element.matches(":disabled") ||
        element.closest("[hidden], [inert], [aria-hidden='true']")
      ) {
        return false;
      }

      const style = window.getComputedStyle(element);
      return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
    });
}

function focusWithoutScrolling(element: HTMLElement | null) {
  if (!element || !element.isConnected) {
    return;
  }

  element.focus({ preventScroll: true });
}
